import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../../../../redux/store";
import { updateConfig } from "./posConfigSlice";
import { POSPaymentModeItem, POSDisplayName } from "./constants";
import { DraggableCardList } from "./DraggableCardList";
import { getPaymentModeIcon } from "./posIconUtils";
import { useIsItemDisabledInGeneral } from "./useIsItemDisabledInGeneral";

export function PaymentMode() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const items = useSelector(
        (state: RootState) => state.posConfig.config.PaymentMode.items
    );
    const isItemDisabledInGeneral = useIsItemDisabledInGeneral();
    const currentLang = (i18n.language || "fr").toUpperCase() as keyof POSDisplayName;
    const getLocalizedTitle = React.useCallback(
        (item: POSPaymentModeItem) =>
            item.displayName[currentLang] || item.displayName.FR || item.name,
        [currentLang]
    );

    const handleToggle = React.useCallback(
        (index: number) => {
            const item = items[index];
            if (isItemDisabledInGeneral(item.id)) return;
            const activeCount = items.filter(
                (it: POSPaymentModeItem) => it.active
            ).length;
            if (item.isFavorite && item.active) return;
            if (item.active && activeCount <= 1) return;

            const newItems = items.map((it: POSPaymentModeItem, i: number) =>
                i === index ? { ...it, active: !it.active } : { ...it }
            );
            dispatch(
                updateConfig({
                    path: ["PaymentMode", "items"],
                    value: newItems,
                })
            );
        },
        [items, dispatch, isItemDisabledInGeneral]
    );

    const isToggleDisabled = React.useCallback(
        (item: POSPaymentModeItem) =>
            isItemDisabledInGeneral(item.id) ||
            Boolean(item.isFavorite && item.active),
        [isItemDisabledInGeneral]
    );

    const handleReorder = React.useCallback(
        (newItems: POSPaymentModeItem[]) => {
            dispatch(
                updateConfig({
                    path: ["PaymentMode", "items"],
                    value: newItems,
                })
            );
        },
        [dispatch]
    );

    return (
        <div className="h-100 pos-tab-layout__clz">
            <div>
                <div className="d-flex align-items-center cursor__clz pos-section-header__clz">
                    <div className="pos-section-title__clz">
                        {t("Choice of means of payment")}
                    </div>
                </div>
            </div>

            <DraggableCardList
                droppableId="paymentMode"
                items={items}
                getTitle={getLocalizedTitle}
                getIcon={getPaymentModeIcon}
                isToggleDisabled={isToggleDisabled}
                isGreyedOut={(item: POSPaymentModeItem) =>
                    isItemDisabledInGeneral(item.id)
                }
                onReorder={handleReorder}
                onToggle={handleToggle}
                testIdPrefix="payment-mode"
            />
        </div>
    );
}
